import {
  PlayerApi,
  UserApi,
  getApiConfigNext,
  type Player,
  type PlayerCreateRequest,
  type PlayerTeamAssignmentRequest,
  type PlayerUpdateRequest,
  type UserPlayer,
  type UserTeams,
} from '@y-sports/api-client';
import type { AdminPlayerDetail } from '@y-sports/api-client/generated-next-management/src/models';
import { AdminPlayerDetailFromJSON } from '@y-sports/api-client/generated-next-management/src/models/AdminPlayerDetail';
import { TeamDataService } from './TeamDataService';

export interface PlayerManagementTeamOption {
  id: string;
  name: string;
}

export interface PlayerManagementAssignment {
  teamId: string;
  teamName: string;
  playerNumber?: number | null;
}

export interface PlayerManagementPlayer extends UserPlayer {
  teamAssignments: PlayerManagementAssignment[];
}

export interface PlayerManagementData {
  players: PlayerManagementPlayer[];
  teams: PlayerManagementTeamOption[];
}

export const PlayerDataService = {
  getPlayerManagementData: async (email: string): Promise<PlayerManagementData> => {
    const apiConfig = await getApiConfigNext();
    const userApi = new UserApi(apiConfig);

    const [players, userTeams] = await Promise.all([
      userApi.getUserPlayers({ email }),
      userApi.getUserTeams({ email }),
    ]);

    const teams: PlayerManagementTeamOption[] = [];
    const teamIds: string[] = [];
    (userTeams as UserTeams[]).forEach((group) => {
      group.teams?.forEach((team) => {
        if (!team.id || !team.name) {
          return;
        }

        teams.push({
          id: team.id,
          name: team.name,
        });
        teamIds.push(team.id);
      });
    });

    const uniqueTeams = Array.from(new Map(teams.map((team) => [team.id, team])).values());
    const teamPlayersResponses = await Promise.all(
      uniqueTeams.map(async (team) => ({
        team,
        players: (await TeamDataService.getTeamById(team.id)).players || [],
      }))
    );

    const assignmentsByPlayerId = new Map<string, PlayerManagementAssignment[]>();
    teamPlayersResponses.forEach(({ team, players }) => {
      (players as Player[]).forEach((player) => {
        if (!player.id) {
          return;
        }

        const assignments = assignmentsByPlayerId.get(player.id) || [];
        if (!assignments.some((assignment) => assignment.teamId === team.id)) {
          assignments.push({
            teamId: team.id,
            teamName: team.name,
            playerNumber: player.playerNumber,
          });
          assignmentsByPlayerId.set(player.id, assignments);
        }
      });
    });

    const enrichedPlayers = (players as UserPlayer[]).map((player) => {
      const inferredAssignments = player.id ? assignmentsByPlayerId.get(player.id) || [] : [];
      const teamAssignments = inferredAssignments.length > 0
        ? inferredAssignments
        : player.teamId && player.teamName
          ? [{
              teamId: player.teamId,
              teamName: player.teamName,
              playerNumber: player.playerNumber,
            }]
          : [];
      const primaryAssignment = teamAssignments[0];

      return {
        ...player,
        teamId: primaryAssignment?.teamId ?? player.teamId,
        teamName: primaryAssignment?.teamName ?? player.teamName,
        playerNumber: primaryAssignment?.playerNumber ?? player.playerNumber,
        teamAssignments,
      } satisfies PlayerManagementPlayer;
    });

    return {
      players: enrichedPlayers,
      teams: uniqueTeams,
    };
  },

  createPlayer: async (payload: PlayerCreateRequest) => {
    const apiConfig = await getApiConfigNext();
    const api = new PlayerApi(apiConfig);
    return api.createPlayer({ playerCreateRequest: payload });
  },

  updatePlayer: async (playerId: string, payload: PlayerUpdateRequest) => {
    const apiConfig = await getApiConfigNext();
    const api = new PlayerApi(apiConfig);
    return api.updatePlayer({
      id: playerId,
      playerUpdateRequest: payload,
    });
  },

  deletePlayer: async (playerId: string) => {
    const apiConfig = await getApiConfigNext();
    const api = new PlayerApi(apiConfig);
    return api.deletePlayer({ id: playerId });
  },

  assignPlayerToTeam: async (playerId: string, payload: PlayerTeamAssignmentRequest) => {
    const apiConfig = await getApiConfigNext();
    const api = new PlayerApi(apiConfig);
    return api.assignPlayerToTeam({
      id: playerId,
      playerTeamAssignmentRequest: payload,
    });
  },

  removePlayerFromTeam: async (playerId: string, teamId: string) => {
    const apiConfig = await getApiConfigNext();
    const api = new PlayerApi(apiConfig);
    return api.removePlayerFromTeam({
      id: playerId,
      removePlayerFromTeamRequest: { teamId },
    });
  },

  getPlayerDetail: async (playerId: string): Promise<AdminPlayerDetail> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/players/${encodeURIComponent(playerId)}/stats`, {
      method: 'GET',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch player detail (${response.status})`);
    }

    const rawDetail = await response.json();
    const parsedDetail = AdminPlayerDetailFromJSON(rawDetail);

    return {
      ...parsedDetail,
      trainings: parsedDetail.trainings.map((record, index) => ({
        ...record,
        late: rawDetail?.trainings?.[index]?.late,
        sessionId: rawDetail?.trainings?.[index]?.sessionId,
      })),
    } as AdminPlayerDetail;
  },
};
